import React, { useEffect, useState } from "react";
import { Card, Form, Pagination } from "react-bootstrap";
import useFetch from "hooks/useFetch";
import UsersSkeleton from "./UsersSkeleton/UsersSkeleton";
import { Header } from "stories";

interface User {
  uid: string;
  email: string;
  username: string;
  allowedOperations: string;
}

const Users: React.FC = () => {
  const { execute, isLoading } = useFetch();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 10;
  const maxVisiblePages = 15;

  const fetchUsers = async () => {
    try {
      const response = (await execute("/api/v1/users", "GET")) as User[];
      if (response) {
        const validUsers = response.filter(
          (user) => user.username?.toLowerCase() !== "notset",
        );
        setUsers(validUsers);
        setFilteredUsers(validUsers);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const lowerSearchTerm = searchTerm.toLowerCase();
    const filtered = users.filter(
      (user) =>
        user.email?.toLowerCase().includes(lowerSearchTerm) ||
        user.username?.toLowerCase().includes(lowerSearchTerm),
    );
    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [searchTerm, users]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const renderPagination = () => {
    const pages = [];
    const ellipsis = <Pagination.Ellipsis disabled />;

    if (currentPage > 1) {
      pages.push(
        <Pagination.Prev
          key="prev"
          onClick={() => handlePageChange(currentPage - 1)}
        >
          Forrige
        </Pagination.Prev>,
      );
    }

    pages.push(
      <Pagination.Item
        key={1}
        active={currentPage === 1}
        onClick={() => handlePageChange(1)}
      >
        1
      </Pagination.Item>,
    );

    if (currentPage > maxVisiblePages) {
      pages.push(ellipsis);
    }

    const startPage = Math.max(
      2,
      currentPage - Math.floor(maxVisiblePages / 2),
    );
    const endPage = Math.min(
      totalPages - 1,
      currentPage + Math.floor(maxVisiblePages / 2),
    );

    for (let page = startPage; page <= endPage; page++) {
      pages.push(
        <Pagination.Item
          key={page}
          active={currentPage === page}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>,
      );
    }

    if (currentPage < totalPages - maxVisiblePages + 1) {
      pages.push(ellipsis);
    }

    if (totalPages > 1) {
      pages.push(
        <Pagination.Item
          key={totalPages}
          active={currentPage === totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>,
      );
    }

    if (currentPage < totalPages) {
      pages.push(
        <Pagination.Next
          key="next"
          onClick={() => handlePageChange(currentPage + 1)}
        >
          Neste
        </Pagination.Next>,
      );
    }

    return <Pagination className="justify-content-center">{pages}</Pagination>;
  };

  if (isLoading) {
    return <UsersSkeleton />;
  }

  return (
    <div className="container mt-4">
      <Header title="Brukere" />
      <br />
      <Form.Control
        type="text"
        placeholder="Søk etter e-post eller navn..."
        className="mb-4"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <div className="user-list">
        {currentItems.map((user) => (
          <Card key={user.uid} className="mb-3">
            <Card.Header>
              <strong>{user.username}</strong>
            </Card.Header>
            <Card.Body>
              <p>
                <strong>E-post:</strong> {user.email}
              </p>
            </Card.Body>
          </Card>
        ))}
      </div>

      <div className="mt-4">{renderPagination()}</div>
    </div>
  );
};

export default Users;
